import { useEffect, useState } from 'react';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button, Input, MultiSelect, Spinner } from '@/components/ui';
import {
  previewAudience,
  setPolicyAudience,
  type Policy,
  type PolicyAudience,
  type PolicyContext,
} from '@/api/services/hrPolicies.service';
import { listHrStructure, type HrStructureKind } from '@/api/services/hr-structure.service';
import { listCompanyEmployments } from '@/api/services/employment.service';
import { useCompanyNavList } from '@/features/companies/hooks/useCompanyNavList';
import { getErrorMessage } from '@/lib/getErrorMessage';

const DIMENSIONS: Array<{
  key: keyof Pick<
    PolicyAudience,
    'departmentIds' | 'teamIds' | 'gradeIds' | 'jobTitleIds' | 'employmentTypeIds'
  >;
  kind: HrStructureKind;
  label: string;
}> = [
  { key: 'departmentIds', kind: 'department', label: 'Departments' },
  { key: 'teamIds', kind: 'team', label: 'Teams' },
  { key: 'gradeIds', kind: 'grade', label: 'Grades' },
  { key: 'jobTitleIds', kind: 'job_title', label: 'Job titles' },
  { key: 'employmentTypeIds', kind: 'employment_type', label: 'Employment types' },
];

const sameList = (left: string[], right: string[]) =>
  left.length === right.length && [...left].sort().join() === [...right].sort().join();

/**
 * Who a policy applies to. Changes take effect on the published version at
 * once: new members are asked to acknowledge, and people who no longer match
 * lose only an outstanding request. Acknowledgements already given stay.
 */
export function PolicyAudiencePanel({
  context,
  policy,
  editable,
  onChanged,
}: {
  context: PolicyContext;
  policy: Policy;
  editable: boolean;
  onChanged: () => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const organizationPolicy = !policy.ownerCompanyId;
  const [audience, setAudience] = useState<PolicyAudience>(policy.audience);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [count, setCount] = useState<number | null>(null);
  const { data: companyData } = useCompanyNavList();
  const companies = Array.isArray(companyData) ? companyData : [];
  const companyName = (id: string) =>
    companies.find((company) => company._id === id)?.name ?? 'Company';

  const structureQueries = useQueries({
    queries: audience.companyIds.map((companyId) => ({
      queryKey: ['hr-structure', companyId],
      queryFn: () => listHrStructure(companyId),
      staleTime: 60_000,
    })),
  });
  const peopleQueries = useQueries({
    queries: audience.companyIds.map((companyId) => ({
      queryKey: ['hr-policy-audience-people', companyId],
      queryFn: () => listCompanyEmployments(companyId, 1, 100),
      staleTime: 60_000,
    })),
  });
  const structures = structureQueries.flatMap((query) =>
    Array.isArray(query.data) ? query.data : []
  );
  const people = new Map<string, string>();
  for (const query of peopleQueries) {
    for (const row of Array.isArray(query.data?.data) ? query.data.data : []) {
      people.set(
        row.userId,
        row.employee ? `${row.employee.firstName} ${row.employee.lastName}`.trim() : row.userId
      );
    }
  }
  const personOptions = [...people].map(([value, label]) => ({ value, label }));
  const multiCompany = audience.companyIds.length > 1;

  const changed = (Object.keys(audience) as Array<keyof PolicyAudience>).some(
    (key) => !sameList(audience[key], policy.audience[key] ?? [])
  );

  // Live size estimate, debounced so typing into selectors does not flood it.
  const { mutate: estimate } = useMutation({
    mutationFn: () => previewAudience(context, audience),
    onSuccess: (result) => setCount(result.total),
    onError: () => setCount(null),
  });
  useEffect(() => {
    // Only the owner can preview: another company's HR cannot evaluate an
    // organization audience.
    if (!editable || audience.companyIds.length === 0) return;
    const timer = window.setTimeout(() => estimate(), 300);
    return () => window.clearTimeout(timer);
  }, [audience, editable, estimate]);

  const save = useMutation({
    mutationFn: () => setPolicyAudience(context, policy._id, audience, reason.trim() || undefined),
    onSuccess: (result) => {
      const reconciled = result.reconciled;
      toast.success(
        reconciled
          ? `Audience updated: ${reconciled.assigned ?? 0} added, ${reconciled.removed ?? 0} no longer asked.`
          : 'Audience updated. It applies when the policy is published.'
      );
      setReason('');
      void queryClient.invalidateQueries({ queryKey: ['hr-policy-report'] });
      onChanged();
    },
    onError: (mutationError) => setError(getErrorMessage(mutationError)),
  });

  const loading = structureQueries.some((query) => query.isLoading);

  return (
    <div className="space-y-4">
      {organizationPolicy && (
        <MultiSelect
          label="Companies"
          value={audience.companyIds}
          disabled={!editable}
          onChange={(companyIds) => setAudience({ ...audience, companyIds })}
          options={companies.map((company) => ({ value: company._id, label: company.name }))}
        />
      )}
      {loading ? (
        <div className="flex justify-center py-6" role="status">
          <Spinner />
          <span className="sr-only">Loading structure</span>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {DIMENSIONS.map(({ key, kind, label }) => {
            const options = structures
              .filter((item) => item.kind === kind && item.active !== false)
              .map((item) => ({
                value: item._id,
                label: multiCompany ? `${item.name} · ${companyName(item.companyId)}` : item.name,
              }));
            if (options.length === 0 && audience[key].length === 0) return null;
            return (
              <MultiSelect
                key={key}
                label={label}
                placeholder="Any"
                value={audience[key]}
                disabled={!editable}
                onChange={(value) => setAudience({ ...audience, [key]: value })}
                options={options}
              />
            );
          })}
          <MultiSelect
            label="Also include"
            placeholder="Nobody"
            value={audience.includeUserIds}
            disabled={!editable}
            onChange={(includeUserIds) => setAudience({ ...audience, includeUserIds })}
            options={personOptions}
          />
          <MultiSelect
            label="Exclude"
            placeholder="Nobody"
            value={audience.excludeUserIds}
            disabled={!editable}
            onChange={(excludeUserIds) => setAudience({ ...audience, excludeUserIds })}
            options={personOptions}
          />
        </div>
      )}
      {editable && (
      <p className="text-sm text-gray-700 dark:text-gray-200" role="status" aria-live="polite">
        {audience.companyIds.length === 0
          ? 'Choose at least one company.'
          : count === null
            ? 'Counting…'
            : `${count} ${count === 1 ? 'employee matches' : 'employees match'} today.`}
      </p>
      )}
      <p className="text-xs text-gray-600 dark:text-gray-300">
        Filters narrow the audience: an employee must match every filter you set. People joining,
        moving, or leaving later are added or removed automatically. Acknowledgements already given
        are never changed.
      </p>
      {editable && changed && (
        <div className="space-y-3 border-t border-gray-200 pt-4 dark:border-gray-700">
          <Input
            id="audience-reason"
            label="Reason for the change (optional)"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          {error && (
            <p className="text-sm text-danger" role="alert">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setAudience(policy.audience)}>
              Reset
            </Button>
            <Button
              size="sm"
              disabled={audience.companyIds.length === 0}
              loading={save.isPending}
              onClick={() => save.mutate()}
            >
              Save audience
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
